import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import type { ResetCredentialMode } from '../useResetCredential';

export interface BulkResetResultRow {
    sub: string;
    username: string;
    temp_password?: string;
    ok: boolean;
    error?: string;
}

interface Props {
    results: BulkResetResultRow[];
    /** EAA-1779: which credential the run used — decides whether there is a secret to list. */
    mode: ResetCredentialMode;
    onDismiss: () => void;
}

function toCsv(rows: BulkResetResultRow[]): string {
    const header = 'username,temp_password\n';
    const body = rows
        .filter((r) => r.ok && r.temp_password)
        .map((r) => `${r.username},${r.temp_password}`)
        .join('\n');
    return header + body;
}

/**
 * EAA-1820: "Bulk reset produces one downloadable list, shown once — never
 * emailed from the portal." The one-time temp passwords are collected into ONE
 * list here, rendered once, with an explicit "Đã lưu xong — ẩn thông tin" dismiss —
 * same one-time-secret contract as the single-account reset panel, N rows instead of 1.
 *
 * EAA-1779: in `admin_set` mode there is no secret to collect — every account got
 * the password the operator typed, and the server never sends it back — so the
 * panel drops the password column and the CSV download entirely and reports only
 * which rows landed. Rendering an empty password column there would read as "the
 * reset produced nothing".
 *
 * EAA-1779 (2026-09-21): the run is ONE `POST /accounts/bulk-reset-password` now,
 * and this panel renders inside a dialog. It used to be appended after the
 * fit-to-shell page, where 100 result rows painted over the toolbar and off the
 * bottom of the viewport. The list is capped in height and scrolls; in admin-set
 * mode the usernames sit behind a collapsed "Xem danh sách" so the dialog opens on
 * the summary, not on a hundred identical rows.
 *
 * EAA-1804: both actions are the kit `Button`; `.portal-admin-secret button` paints
 * the legacy portal-btn skin on any bare button inside it, so a bare `<button>` here
 * would render as a portal-btn without ever declaring one.
 */
export function BulkResetResultPanel({ results, mode, onDismiss }: Props) {
    const { t } = useTranslation();
    const okRows = results.filter((r) => r.ok);
    const failedRows = results.filter((r) => !r.ok);
    const revealsSecrets = mode !== 'admin_set';

    function download() {
        const blob = new Blob([toCsv(results)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mat-khau-tam-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    const usernameList = (
        <div className="portal-table-scroll acc-bulk-result__scroll">
            <table className="portal-admin-table">
                <thead>
                    <tr>
                        <th>{t('console.accounts.bulkResetResult.columnUsername')}</th>
                        {revealsSecrets && <th>{t('console.accounts.bulkResetResult.columnTempPassword')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {okRows.map((r) => (
                        <tr key={r.sub}>
                            <td>
                                <code>{r.username}</code>
                            </td>
                            {revealsSecrets && (
                                <td>
                                    <code>{r.temp_password}</code>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="portal-admin-secret acc-bulk-result" role="alert">
            <strong>
                {revealsSecrets
                    ? t('console.accounts.bulkResetResult.heading', { count: okRows.length })
                    : t('console.accounts.bulkResetResult.headingAdminSet', { count: okRows.length })}
            </strong>
            <p className="acc-bulk-result__summary">
                {t('console.accounts.bulkResetResult.summary', { ok: okRows.length, failed: failedRows.length })}
            </p>
            {revealsSecrets ? (
                usernameList
            ) : (
                okRows.length > 0 && (
                    <details className="acc-bulk-result__details">
                        <summary>{t('console.accounts.bulkResetResult.showAccounts', { count: okRows.length })}</summary>
                        {usernameList}
                    </details>
                )
            )}
            {failedRows.length > 0 && (
                <p className="portal-admin-error">
                    {t('console.accounts.bulkResetResult.failedNotice', {
                        count: failedRows.length,
                        usernames: failedRows.map((r) => r.username).join(', '),
                    })}
                </p>
            )}
            <div className="portal-admin-form-actions">
                {revealsSecrets && (
                    <Button onClick={download} disabled={okRows.length === 0}>
                        {t('console.accounts.bulkResetResult.downloadButton')}
                    </Button>
                )}
                <Button type="primary" onClick={onDismiss}>
                    {t('console.accounts.bulkResetResult.dismissButton')}
                </Button>
            </div>
        </div>
    );
}
